import React, { memo, useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { NodeStatus } from '@/types';

interface MediaPreviewProps {
  type: 'image' | 'video' | 'audio';
  src: string;
  status: NodeStatus;
  error?: string;
  onExpand?: () => void;
}

const MediaPreviewInner: React.FC<MediaPreviewProps> = ({
  type,
  src,
  status,
  error,
  onExpand,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (type !== 'image' || !containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [type]);

  if (status === NodeStatus.WORKING) {
    return (
      <div className="flex items-center justify-center h-full bg-[#262626]">
        <div className="animate-pulse text-cyan-400">生成中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-[#262626]">
        <div className="text-red-400 text-xs">{error}</div>
      </div>
    );
  }

  if (!src) {
    return (
      <div className="flex items-center justify-center h-full bg-[#262626]">
        <div className="text-slate-500 text-xs">等待生成</div>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div ref={containerRef} className="w-full h-full">
        {isVisible && (
          <img
            src={src}
            alt="Generated"
            loading="lazy"
            className={`w-full h-full object-cover cursor-default transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
            onClick={onExpand}
          />
        )}
        {!isLoaded && isVisible && (
          <div className="flex items-center justify-center h-full bg-[#262626]">
            <div className="animate-pulse text-slate-500 text-xs">加载中...</div>
          </div>
        )}
      </div>
    );
  }

  if (type === 'video') {
    return (
      <video
        key={src}
        src={src}
        className="w-full h-full object-cover"
        controls
        loop
        playsInline
        preload="metadata"
      />
    );
  }

  if (type === 'audio') {
    return (
      <div className="flex items-center justify-center h-full bg-[#262626]">
        <audio src={src} controls className="w-full" />
      </div>
    );
  }

  return null;
};

export const MediaPreview = memo(MediaPreviewInner, (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.src === nextProps.src &&
    prevProps.status === nextProps.status &&
    prevProps.error === nextProps.error
  );
});